import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Search, CheckCircle, XCircle, Edit, Eye, Archive, User, Package, Loader2, Lock, Unlock, Hand } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { TestTextarea as Textarea } from '@/components/ui/test-textarea';
import { useToast } from '@/components/ui/use-toast';
import { getRecolecciones, updateRecoleccion } from '../services/deposito.services';

const CasillerosGestion = ({ idPlanta, plantaNombre }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [recolecciones, setRecolecciones] = useState([]);
  const [selectedCasillero, setSelectedCasillero] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' o 'list'
  const [casilleros, setCasilleros] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentCasillero, setCurrentCasillero] = useState(null);

  // Cargar casilleros y recolecciones
  const loadCasilleros = useCallback(async () => {
    setLoading(true);
    try {
      // Cargar todos los casilleros desde el backend
      const casilleroResponse = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/casillero`);
      const casilleroData = await casilleroResponse.json();
      setCasilleros(casilleroData.data || []);

      // Cargar TODAS las recolecciones (verificadas y pendientes)
      if (idPlanta) {
        const recoleccionResponse = await getRecolecciones({ 
          idPlanta
        });
        setRecolecciones(recoleccionResponse?.data || []);
      }
    } catch (error) {
      console.error('Error cargando casilleros:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los casilleros',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [idPlanta, toast]);

  useEffect(() => {
    loadCasilleros();
  }, [loadCasilleros]);

  // Obtener el estado de un casillero específico
  const getCasilleroInfo = (numeroCasillero) => {
    const recoleccion = recolecciones.find(r => r.casillero === numeroCasillero);
    const casillero = casilleros.find(c => c.numero === numeroCasillero);
    
    return {
      casillero,
      recoleccion,
      estado: casillero?.estado || 'disponible',
      empleado: recoleccion?.empleado
    };
  };

  // Filtrar casilleros por búsqueda
  const filteredCasilleros = casilleros.filter(casillero => {
    if (!searchTerm) return true;
    const info = getCasilleroInfo(casillero.numero);
    return (
      casillero.numero.toLowerCase().includes(searchTerm.toLowerCase()) ||
      info.empleado?.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      info.empleado?.cedula?.includes(searchTerm)
    );
  });

  const handleVerificarRecoleccion = async (recoleccion, aprobar, casillero) => {
    setLoading(true);
    try {
      // Actualizar estado de la recolección
      const recoleccionActualizada = {
        ...recoleccion,
        estado: aprobar ? 'verificado' : 'rechazado'
      };

      await updateRecoleccion(recoleccion._id || recoleccion.id, recoleccionActualizada);

      // Liberar el casillero (la ropa sucia ya fue recogida)
      if (casillero && aprobar) {
        const casilleroResponse = await fetch(
          `${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/casillero/update`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
              id: casillero._id || casillero.id,
              estado: 'disponible' 
            })
          }
        );

        if (!casilleroResponse.ok) {
          throw new Error('Error al liberar casillero');
        }
      }

      toast({
        title: aprobar ? 'Recolección Verificada' : 'Recolección Rechazada',
        description: aprobar 
          ? 'Las prendas han sido verificadas y el casillero está disponible'
          : 'La recolección ha sido rechazada'
      });

      // Recargar datos
      await loadCasilleros();
      setDialogOpen(false);

    } catch (error) {
      console.error('Error verificando recolección:', error);
      toast({
        title: 'Error',
        description: error.message || 'No se pudo procesar la verificación',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLiberarCasillero = async (casillero, recoleccion) => {
    setLoading(true);
    try {
      // Actualizar estado del casillero a disponible
      const casilleroResponse = await fetch(
        `${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/casillero/update`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            id: casillero._id || casillero.id,
            estado: 'disponible' 
          })
        }
      );

      if (!casilleroResponse.ok) throw new Error('Error al liberar casillero');

      // Marcar recolección como entregada
      if (recoleccion) {
        const recoleccionActualizada = {
          ...recoleccion,
          estado: 'entregado',
          fechaEntrega: new Date().toISOString()
        };
        await updateRecoleccion(recoleccion._id || recoleccion.id, recoleccionActualizada);
      }

      toast({
        title: 'Casillero Liberado',
        description: `El casillero ${casillero.numero} está ahora disponible`
      });

      // Recargar datos
      await loadCasilleros();
      setDialogOpen(false);

    } catch (error) {
      console.error('Error liberando casillero:', error);
      toast({
        title: 'Error',
        description: error.message || 'No se pudo liberar el casillero',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditPrenda = (recoleccionId, prenda) => {
    setEditingPrenda({ recoleccionId, ...prenda });
  };

  const handleSaveEditPrenda = async () => {
    if (!editingPrenda) return;

    setLoading(true);
    try {
      // Aquí iría la llamada al backend
      await new Promise(resolve => setTimeout(resolve, 500));

      setRecolecciones(prev => prev.map(r => 
        r.id === editingPrenda.recoleccionId
          ? {
              ...r,
              prendas: r.prendas.map(p => 
                p.id === editingPrenda.id ? editingPrenda : p
              )
            }
          : r
      ));

      toast({
        title: 'Prenda Actualizada',
        description: 'Los datos de la prenda han sido actualizados'
      });

      setEditingPrenda(null);

    } catch (error) {
      console.error('Error actualizando prenda:', error);
      toast({
        title: 'Error',
        description: 'No se pudo actualizar la prenda',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const getEstadoBadge = (estado) => {
    switch (estado) {
      case 'pendiente_verificacion':
        return <Badge variant="outline" className="text-yellow-600 border-yellow-600">Pendiente Verificación</Badge>;
      case 'verificado':
        return <Badge variant="outline" className="text-green-600 border-green-600">Verificado</Badge>;
      case 'pendiente_recoger':
        return <Badge variant="outline" className="text-purple-600 border-purple-600">Pendiente Recoger</Badge>;
      case 'entregado':
        return <Badge variant="outline" className="text-blue-600 border-blue-600">Entregado</Badge>;
      case 'rechazado':
        return <Badge variant="outline" className="text-red-600 border-red-600">Rechazado</Badge>;
      default:
        return <Badge variant="outline">Desconocido</Badge>;
    }
  };

  // Obtener estadísticas
  const stats = {
    total: casilleros.length,
    disponibles: casilleros.filter(c => {
      const info = getCasilleroInfo(c.numero);
      return !info.recoleccion && c.estado === 'disponible';
    }).length,
    pendientes: recolecciones.filter(r => r.estado === 'pendiente_verificacion').length,
    verificados: recolecciones.filter(r => r.estado === 'verificado').length,
    pendienteRecoger: recolecciones.filter(r => r.estado === 'pendiente_recoger').length,
    ocupados: casilleros.filter(c => c.estado === 'ocupado').length,
    mantenimiento: casilleros.filter(c => c.estado === 'mantenimiento').length
  };

  return (
    <div className="space-y-6">
      {/* Header con información y estadísticas */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Archive className="h-5 w-5 text-purple-500" />
            Gestión de Casilleros
          </CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Vista visual de todos los casilleros. Haz click en un casillero para ver detalles.
          </p>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Estadísticas */}
            <div className="grid grid-cols-6 gap-3">
              <div className="flex items-center gap-2 p-3 bg-gray-100 dark:bg-gray-800 rounded-lg">
                <Archive className="h-5 w-5 text-gray-600" />
                <div>
                  <p className="text-xs text-muted-foreground">Total</p>
                  <p className="text-lg font-bold">{stats.total}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 p-3 bg-green-100 dark:bg-green-900/20 rounded-lg">
                <Unlock className="h-5 w-5 text-green-600" />
                <div>
                  <p className="text-xs text-muted-foreground">Disponibles</p>
                  <p className="text-lg font-bold text-green-600">{stats.disponibles}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 p-3 bg-yellow-100 dark:bg-yellow-900/20 rounded-lg">
                <Package className="h-5 w-5 text-yellow-600" />
                <div>
                  <p className="text-xs text-muted-foreground">Pendientes</p>
                  <p className="text-lg font-bold text-yellow-600">{stats.pendientes}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 p-3 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                <CheckCircle className="h-5 w-5 text-blue-600" />
                <div>
                  <p className="text-xs text-muted-foreground">Verificados</p>
                  <p className="text-lg font-bold text-blue-600">{stats.verificados}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 p-3 bg-red-100 dark:bg-red-900/20 rounded-lg">
                <Lock className="h-5 w-5 text-red-600" />
                <div>
                  <p className="text-xs text-muted-foreground">Ocupados</p>
                  <p className="text-lg font-bold text-red-600">{stats.ocupados}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 p-3 bg-orange-100 dark:bg-orange-900/20 rounded-lg">
                <XCircle className="h-5 w-5 text-orange-600" />
                <div>
                  <p className="text-xs text-muted-foreground">Mantenimiento</p>
                  <p className="text-lg font-bold text-orange-600">{stats.mantenimiento}</p>
                </div>
              </div>
            </div>

            {/* Buscador */}
            <div className="flex gap-4">
              <div className="flex-1">
                <Input
                  placeholder="Buscar por número de casillero, nombre o cédula..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full"
                />
              </div>
            </div>

            {/* Leyenda */}
            <div className="flex items-center gap-4 text-sm flex-wrap">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-green-500 rounded"></div>
                <span>Disponible</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-yellow-500 rounded"></div>
                <span>Pendiente Verificación</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-blue-500 rounded"></div>
                <span>Verificado</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-purple-500 rounded"></div>
                <span>Pendiente Recoger</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-red-500 rounded"></div>
                <span>Ocupado</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-orange-500 rounded"></div>
                <span>Mantenimiento</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Estado de carga */}
      {loading && (
        <Card>
          <CardContent className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary mr-2" />
            <span className="text-muted-foreground">Cargando casilleros...</span>
          </CardContent>
        </Card>
      )}

      {/* Grid de Casilleros */}
      {!loading && (
        <Card>
          <CardContent className="p-6">
            <div className="grid grid-cols-7 gap-3">
              {filteredCasilleros.map((casillero) => {
                const info = getCasilleroInfo(casillero.numero);
                const isMantenimiento = info.estado === 'mantenimiento';
                const isPendiente = info.recoleccion?.estado === 'pendiente_verificacion';
                const isVerificado = info.recoleccion?.estado === 'verificado';
                const isPendienteRecoger = info.recoleccion?.estado === 'pendiente_recoger';
                const isOcupado = info.estado === 'ocupado';
                
                // Determinar color y estado
                let colorClass = '';
                let icon = null;
                
                if (isMantenimiento) {
                  colorClass = 'bg-orange-100 border-orange-500 hover:bg-orange-200 dark:bg-orange-900/30';
                  icon = <XCircle className="h-4 w-4 text-orange-600" />;
                } else if (isPendienteRecoger) {
                  colorClass = 'bg-purple-100 border-purple-500 hover:bg-purple-200 dark:bg-purple-900/30';
                  icon = <Hand className="h-4 w-4 text-purple-600" />;
                } else if (isOcupado) {
                  colorClass = 'bg-red-100 border-red-500 hover:bg-red-200 dark:bg-red-900/30';
                  icon = <Lock className="h-4 w-4 text-red-600" />;
                } else if (isPendiente) {
                  colorClass = 'bg-yellow-100 border-yellow-500 hover:bg-yellow-200 dark:bg-yellow-900/30';
                  icon = <Package className="h-4 w-4 text-yellow-600" />;
                } else if (isVerificado) {
                  colorClass = 'bg-blue-100 border-blue-500 hover:bg-blue-200 dark:bg-blue-900/30';
                  icon = <CheckCircle className="h-4 w-4 text-blue-600" />;
                } else {
                  colorClass = 'bg-green-100 border-green-500 hover:bg-green-200 dark:bg-green-900/30';
                  icon = <Unlock className="h-4 w-4 text-green-600" />;
                }
                
                return (
                  <Dialog key={casillero._id || casillero.numero}>
                    <DialogTrigger asChild>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className={`
                          aspect-square p-4 rounded-lg border-2 transition-all
                          flex flex-col items-center justify-center gap-1
                          ${colorClass}
                        `}
                      >
                        <span className="font-bold text-lg">{casillero.numero}</span>
                        {icon}
                      </motion.button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                      <DialogHeader>
                        <DialogTitle>Casillero {casillero.numero}</DialogTitle>
                        <DialogDescription>
                          {casillero.ubicacion}
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">Estado:</span>
                          {isOcupado && <Badge className="bg-red-500">Ocupado</Badge>}
                          {isMantenimiento && <Badge className="bg-yellow-500">Mantenimiento</Badge>}
                          {!isOcupado && !isMantenimiento && <Badge className="bg-green-500">Disponible</Badge>}
                        </div>
                        
                        {info.recoleccion && (
                          <div className="space-y-3 border-t pt-4">
                            <div className="flex items-center justify-between">
                              <h4 className="font-semibold">Información de Recolección</h4>
                              {info.recoleccion.estado === 'pendiente_verificacion' && (
                                <Badge variant="outline" className="text-yellow-600 border-yellow-600">
                                  Pendiente Verificación
                                </Badge>
                              )}
                              {info.recoleccion.estado === 'verificado' && (
                                <Badge variant="outline" className="text-green-600 border-green-600">
                                  Verificado
                                </Badge>
                              )}
                            </div>
                            
                            <div className="grid gap-2">
                              <div className="flex items-center gap-2">
                                <User className="h-4 w-4 text-muted-foreground" />
                                <span className="font-medium">{info.empleado?.nombre}</span>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <span>Cédula: {info.empleado?.cedula}</span>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <Package className="h-4 w-4" />
                                <span>{info.recoleccion.cantidad_prendas} prenda(s)</span>
                              </div>
                            </div>
                            
                            {info.recoleccion.prendas && info.recoleccion.prendas.length > 0 && (
                              <div className="space-y-2">
                                <h5 className="font-medium text-sm">Prendas:</h5>
                                <div className="space-y-1">
                                  {info.recoleccion.prendas.map((prenda, idx) => (
                                    <div key={idx} className="text-sm p-2 bg-gray-50 dark:bg-gray-800 rounded">
                                      <span className="font-medium">{prenda.nombre}</span> - {prenda.descripcion} (x{prenda.cantidad})
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Botones de acción según el estado */}
                            <div className="flex gap-2 pt-4 border-t">
                              {info.recoleccion.estado === 'pendiente_verificacion' && (
                                <>
                                  <Button
                                    onClick={() => handleVerificarRecoleccion(info.recoleccion, true, casillero)}
                                    disabled={loading}
                                    className="flex-1 bg-green-600 hover:bg-green-700"
                                  >
                                    <CheckCircle className="h-4 w-4 mr-2" />
                                    Verificar y Aprobar
                                  </Button>
                                  <Button
                                    variant="outline"
                                    onClick={() => handleVerificarRecoleccion(info.recoleccion, false, casillero)}
                                    disabled={loading}
                                    className="flex-1 text-red-600 border-red-600 hover:bg-red-50"
                                  >
                                    <XCircle className="h-4 w-4 mr-2" />
                                    Rechazar
                                  </Button>
                                </>
                              )}
                              
                              {info.recoleccion.estado === 'verificado' && (
                                <Button
                                  onClick={() => handleLiberarCasillero(casillero, info.recoleccion)}
                                  disabled={loading}
                                  className="w-full bg-blue-600 hover:bg-blue-700"
                                >
                                  <Unlock className="h-4 w-4 mr-2" />
                                  Liberar Casillero (Entregar Ropa)
                                </Button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </DialogContent>
                  </Dialog>
                );
              })}
            </div>
            
            {filteredCasilleros.length === 0 && (
              <div className="text-center py-8">
                <Archive className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">
                  No se encontraron casilleros que coincidan con la búsqueda
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CasillerosGestion;
